<template>
  <!-- 产品详情页容器 -->
  <div class="product-detail-container" style="margin-top: 150px;">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="10" animated />
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <el-empty description="产品信息加载失败">
        <el-button type="primary" @click="retryLoad">重新加载</el-button>
      </el-empty>
    </div>

    <!-- 无数据状态 -->
    <div v-else-if="!product" class="no-data-container">
      <el-empty description="未找到相关产品信息" />
    </div>

    <!-- 产品内容 -->
    <div v-else class="product-content">
      <!-- 产品头部区域：图片和基本信息 -->
      <div class="product-header">
        <!-- 产品主图 -->
        <div class="product-image">
          <el-image
              :src="product.imgUrl"
              :alt="product.name || '产品图片'"
              fit="cover"
              class="product-main-image"
          >
            <template #error>
              <div class="image-error">
                <el-icon><Picture /></el-icon>
                <span>图片加载失败</span>
              </div>
            </template>
          </el-image>
        </div>

        <!-- 产品信息区域 -->
        <div class="product-info">
          <!-- 产品名称 -->
          <h1>{{ product.name || '未知产品' }}</h1>

          <!-- 产品标签列表 -->
          <div class="product-tags">
            <span
                v-for="(tag, index) in processedTags"
                :key="index"
                class="tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>
      </div>

      <!-- 产品内容标签页 -->
      <div class="product-tabs">
        <!-- 标签页导航 -->
        <div class="tabs-header">
          <button
              :class="{ active: activeTab === 'description' }"
              @click="activeTab = 'description'"
          >
            产品介绍
          </button>
          <button
              :class="{ active: activeTab === 'details' }"
              @click="activeTab = 'details'"
          >
            产品详情
          </button>
        </div>

        <!-- 标签页内容区域 -->
        <div class="tabs-content">
          <!-- 产品介绍标签页 -->
          <div v-show="activeTab === 'description'" class="tab-pane">
            <div
                class="description"
                v-html="product.introduce || '暂无产品介绍'"
            ></div>
          </div>

          <!-- 产品详情标签页（图片形式） -->
          <div v-show="activeTab === 'details'" class="tab-pane">
            <div v-if="product.parameterImg" class="details-image">
              <el-image
                  :src="product.parameterImg"
                  :alt="product.name + '详情'"
                  fit="contain"
                  class="details-main-image"
                  :preview-src-list="[product.parameterImg]"
              >
                <template #error>
                  <div class="image-error">
                    <el-icon><Picture /></el-icon>
                    <span>详情图片加载失败</span>
                  </div>
                </template>
              </el-image>
            </div>
            <div v-else class="no-details">
              <el-empty description="暂无产品详情图片" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import serverApi from "@/api/server";
import { Picture } from '@element-plus/icons-vue';

export default {
  name: 'ProductDetail',
  components: {
    Picture
  },
  props: {
    productData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeTab: 'description',
      product: null, // 初始化为 null
      loading: true, // 添加加载状态
      error: false   // 添加错误状态
    }
  },
  computed: {
    // 处理标签数据
    processedTags() {
      if (!this.product || !this.product.label) {
        return [];
      }

      // 如果是数组，直接返回
      if (Array.isArray(this.product.label)) {
        return this.product.label;
      }

      // 如果是字符串，按 | 分割
      if (typeof this.product.label === 'string') {
        return this.product.label
            .split("|")
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0);
      }

      return [];
    }
  },
  created() {
    this.getProductById(this.$route.query.id);
  },
  methods: {
    async getProductById(productId) {
      // 重置状态
      this.loading = true;
      this.error = false;

      // 验证产品ID
      if (!productId) {
        console.error('产品ID不能为空');
        this.loading = false;
        this.error = true;
        return;
      }

      try {
        const res = await serverApi.getProductById(productId);

        // 检查响应数据
        if (res && res.data && res.data.data) {
          this.product = res.data.data;
        } else {
          console.warn('未获取到产品数据');
          this.product = null;
        }
      } catch (error) {
        console.error('获取产品信息失败:', error);
        this.error = true;
        this.product = null;
      } finally {
        this.loading = false;
      }
    },

    // 重新加载数据
    retryLoad() {
      this.getProductById(this.$route.query.id);
    }
  },
  watch: {
    // 监听路由变化，当ID改变时重新加载产品
    '$route.query.id'(newId, oldId) {
      if (newId !== oldId) {
        this.getProductById(newId);
      }
    },

    productData: {
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.product = {
            ...this.product,
            ...newVal
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* 产品详情容器样式 */
.product-detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* 加载状态样式 */
.loading-container {
  padding: 40px 0;
}

/* 错误状态样式 */
.error-container, .no-data-container {
  padding: 40px 0;
}

/* 产品内容样式 */
.product-content {
  animation: fadeIn 0.3s ease-in-out;
}

/* 产品头部区域样式 */
.product-header {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  margin-bottom: 40px;
}

/* 产品图片区域样式 */
.product-image {
  flex: 1;
  min-width: 300px;
  max-width: 600px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.product-main-image {
  width: 100%;
  height: 100%;
  display: block;
}

/* 图片错误状态 */
.image-error {
  width: 100%;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  background-color: #f8f9fa;
}

.image-error .el-icon {
  font-size: 40px;
  margin-bottom: 10px;
  color: #c0c4cc;
}

/* 产品信息区域样式 */
.product-info {
  flex: 1;
  min-width: 300px;
  padding: 20px 0;
}

/* 产品名称样式 */
.product-info h1 {
  font-size: 28px;
  margin: 0 0 20px 0;
  color: #333;
  font-weight: 600;
}

/* 产品标签容器样式 */
.product-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

/* 单个标签样式 */
.tag {
  background: #f0f7ff;
  color: #409eff;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  border: 1px solid #d9ecff;
}

/* 标签页整体样式 */
.product-tabs {
  margin-top: 40px;
}

/* 标签页导航样式 */
.tabs-header {
  display: flex;
  border-bottom: 1px solid #e4e7ed;
}

/* 标签页按钮样式 */
.tabs-header button {
  padding: 12px 24px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  position: relative;
  transition: all 0.3s ease;
}

/* 激活状态的标签页按钮样式 */
.tabs-header button.active {
  color: #409eff;
  font-weight: 500;
}

/* 激活状态标签页按钮的下划线 */
.tabs-header button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: #409eff;
}

/* 标签页按钮悬停效果 */
.tabs-header button:hover {
  color: #409eff;
}

/* 标签页内容区域样式 */
.tab-pane {
  padding: 25px 0;
}

/* 产品描述文本样式 */
.description {
  line-height: 1.8;
  color: #555;
  font-size: 16px;
}

/* 详情图片容器样式 */
.details-image {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.details-main-image {
  width: 100%;
  display: block;
}

/* 无详情图片提示 */
.no-details {
  text-align: center;
  padding: 40px 0;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 - 中等屏幕 */
@media (max-width: 992px) {
  .product-header {
    gap: 20px;
  }

  .product-info h1 {
    font-size: 24px;
  }
}

/* 响应式设计 - 平板 */
@media (max-width: 768px) {
  .product-header {
    flex-direction: column;
  }

  .product-image, .product-info {
    width: 100%;
    max-width: 100%;
    margin-top: -20px;
  }

  .product-info {
    padding: 0;
  }
}

/* 响应式设计 - 手机 */
@media (max-width: 480px) {
  .product-detail-container {
    padding: 15px;
  }

  .tabs-header {
    overflow-x: auto;
    white-space: nowrap;
  }

  .tabs-header button {
    padding: 12px 16px;
    font-size: 14px;
  }

  .tag {
    padding: 4px 12px;
    font-size: 12px;
  }
}
</style>
